import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { Auth, getIdToken } from '@angular/fire/auth';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { Employee } from '../../shared/../employee/shared/employee.model';
import { EmployeeService } from '../../shared/../employee/shared/employee.service';
import { RemoveCommasPipe } from '../../shared/remove-commas.pipe';
import { StateManager } from '../../shared/state-manager.class';
import { EmployeeNamesComponentState } from '../../shared/state-interface';

@Component({
    selector: 'app-employee-names-component',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatAutocompleteModule,
        CommonModule,
        RemoveCommasPipe
    ],
    templateUrl: './employee-names.component.html',
    styleUrl: './employee-names.component.css'
})
export class EmployeeNamesComponent {

    @ViewChild("input") userInput: ElementRef<HTMLInputElement> | undefined;
    @Output() messageEvent = new EventEmitter<string>();

    private auth: Auth = inject(Auth);
    private stateManager: StateManager = inject(StateManager);
    private employeeService: EmployeeService = inject(EmployeeService);

    private static _employeeNamesError = "Employee Names error:";

    protected employee: FormControl = new FormControl<string | Employee>("");
    protected filteredEmployees: Employee[] = [];

    private _employeeNames: WritableSignal<Employee[]> = signal([]);
    private _show: WritableSignal<boolean> = signal(true);

    public getState() {
        const state: EmployeeNamesComponentState = {} as EmployeeNamesComponentState;
        state.employeeControl = this.employee;
        state.show = this._show();
        return state;
    }

    public restoreState(state: EmployeeNamesComponentState) {
        this.employee = state.employeeControl;
        this._employeeNames.set(this.stateManager.employeeNames!);
        this._show.set(state.show);
    }

    public init() {
        if (this._employeeNames().length == 0) {
            const employeeNames = this.stateManager.employeeNames;

            if (employeeNames == undefined) {
                this.employee.setValue("Loading...");
                this.loadEmployeeNames();
            } else {
                this._employeeNames.set(employeeNames);
            }
        }
    }

    public get show(): boolean {
        return this._show();
    }

    public set show(value: boolean) {
        this._show.set(value);
    }

    // Note: Must use "arrow" function declaration here
    // in order to have access to 'this'. If using normal
    // function declaration, then 'this' will refer to the
    // mat-select object.
    public displayWithFunc = (employee: Employee) => {
        if (employee instanceof (Employee)) {
            return `${employee.first_name} ${employee.last_name}`;
        } else {
            return employee;
        }
    }

    public filter(): void {
        if (this.userInput != undefined && this._employeeNames().length > 0) {
            const filterValue = this.userInput.nativeElement.value.toLowerCase();
            this.filteredEmployees = this._employeeNames().filter(
                employee =>
                    `${employee.id} ${employee.first_name} ${employee.last_name}`.toLowerCase().includes(filterValue)
            );
        }
    }

    public get employeeCtl(): FormControl {
        return this.employee;
    }

    public get selectedEmployee(): number {
        return this.employee.value instanceof Employee ? this.employee.value.id : -1;
    }

    protected get employeeNames(): Employee[] {
        return this._employeeNames()!;
    }

    protected async loadEmployeeNames() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.employeeService.getEmployeeNames(await getIdToken(currentUser));

            observable.subscribe(response => {
                // For testing to validate proper rendering of form fields
                // when retrieving data over slow internet connections.
                //setTimeout(() => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["get-employee-names"] == "success") {
                        // this.employeeNames is a signal. The view will be refreshed
                        // upon update to the signal.
                        this._employeeNames.set(Employee.toEmployees(json["employee-names"]));
                        this.stateManager.employeeNames = this._employeeNames();
                        this.employee.setValue("");
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${EmployeeNamesComponent._employeeNamesError} ${(<Error>error).message}`;
                    console.error(message);
                    this.messageEvent.emit(message);
                }
                //}, 2500);
            })
        }
    }
}